"use client";

import { useQuery } from "@tanstack/react-query";
import type {
	ChartDataset,
	ChartOptions,
	LegendItem,
	TooltipItem,
} from "chart.js";
import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LinearScale,
	Title,
	Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import cacheKeys from "@/config/cache-keys";
import useFormulas from "@/hooks/useFormulas";
import AppService from "@/services/AppService";

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
);

interface InsurancePayoutChartJSProps {
	currency: "NOK" | "G";
}

const appService = new AppService();

const safeValue = (label: string, value: number): number => {
	if (!Number.isFinite(value)) {
		// eslint-disable-next-line no-console
		console.error("NaN payout value in InsurancePayoutChartJS", {
			label,
			value,
		});
		return 0;
	}
	return value;
};

export function InsurancePayoutChartJS({
	currency,
}: InsurancePayoutChartJSProps) {
	const { t } = useTranslation();

	const formulas = useFormulas();

	const { id } = useParams();

	const query = useQuery({
		queryKey: cacheKeys.CLIENT_DATA,
		queryFn: async () => appService.getClientData(id as string),
		enabled: !!id,
	});

	// useEffect(() => {
	// 	if (query.data) {
	// 		formulas.setInsurances(query.data?.Result.insurances);
	// 		formulas.setAnnualSalaryInput("600000", query.data.Result.gvalue || 1);
	// 	}
	// }, [query.data, formulas]);

	// x-axis labels:
	const labels = [
		[
			`${t("calculator.chart.benefits.accident")}`,
			` (${t("calculator.chart.benefits.work")})`,
		],
		[
			`${t("calculator.chart.benefits.accident")}`,
			` (${t("calculator.chart.benefits.other")})`,
		],
		[
			`${t("calculator.chart.benefits.illness")}`,
			` (${t("calculator.chart.benefits.work")})`,
		],
		[
			`${t("calculator.chart.benefits.illness")}`,
			` (${t("calculator.chart.benefits.other")})`,
		],
	];

	if (query.isLoading) {
		return <div>Loading...</div>;
	}

	if (query.isError) {
		return <div>Error: {query.error.message}</div>;
	}

	if (!query.data) {
		return <div>No data available</div>;
	}

	const gValue = query.data.Result.gvalue;

	const safeInvalidity = (
		label: string,
		at: "work" | "other",
		causedBy: "accident" | "illness",
		show: "employer" | "compulsory",
	) =>
		safeValue(
			label,
			formulas.getInvalidityPayout({ at, causedBy, show, currency }, gValue),
		);

	const safeDisability = (
		label: string,
		at: "work" | "other",
		causedBy: "accident" | "illness",
		show: "employer" | "compulsory",
	) =>
		safeValue(
			label,
			formulas.getDisabilityPayout({ at, causedBy, show, currency }, gValue),
		);

	const safeDeath = (
		label: string,
		at: "work" | "other",
		causedBy: "accident" | "illness",
		show: "employer" | "compulsory",
	) =>
		safeValue(
			label,
			formulas.getDeathPayout({ at, causedBy, show, currency }, gValue),
		);

	// Employer and Compulsory for medical invalidity
	const employerMedicalData = [
		safeInvalidity("employer-medical-accident-work", "work", "accident", "employer"),
		safeInvalidity("employer-medical-accident-other", "other", "accident", "employer"),
		safeInvalidity("employer-medical-illness-work", "work", "illness", "employer"),
		safeInvalidity("employer-medical-illness-other", "other", "illness", "employer"),
	];
	const compulsoryMedicalData = [
		safeInvalidity(
			"compulsory-medical-accident-work",
			"work",
			"accident",
			"compulsory",
		),
		safeInvalidity(
			"compulsory-medical-accident-other",
			"other",
			"accident",
			"compulsory",
		),
		safeInvalidity(
			"compulsory-medical-illness-work",
			"work",
			"illness",
			"compulsory",
		),
		safeInvalidity(
			"compulsory-medical-illness-other",
			"other",
			"illness",
			"compulsory",
		),
	];

	// Employer and Compulsory for occupational invalidity
	const employerOccupationalData = [
		safeDisability(
			"employer-occupational-accident-work",
			"work",
			"accident",
			"employer",
		),
		safeDisability(
			"employer-occupational-accident-other",
			"other",
			"accident",
			"employer",
		),
		safeDisability(
			"employer-occupational-illness-work",
			"work",
			"illness",
			"employer",
		),
		safeDisability(
			"employer-occupational-illness-other",
			"other",
			"illness",
			"employer",
		),
	];
	const compulsoryOccupationalData = [
		safeDisability(
			"compulsory-occupational-accident-work",
			"work",
			"accident",
			"compulsory",
		),
		safeDisability(
			"compulsory-occupational-accident-other",
			"other",
			"accident",
			"compulsory",
		),
		safeDisability(
			"compulsory-occupational-illness-work",
			"work",
			"illness",
			"compulsory",
		),
		safeDisability(
			"compulsory-occupational-illness-other",
			"other",
			"illness",
			"compulsory",
		),
	];

	// Employer and Compulsory for death
	const employerDeathData = [
		safeDeath("employer-death-accident-work", "work", "accident", "employer"),
		safeDeath("employer-death-accident-other", "other", "accident", "employer"),
		safeDeath("employer-death-illness-work", "work", "illness", "employer"),
		safeDeath("employer-death-illness-other", "other", "illness", "employer"),
	];
	const compulsoryDeathData = [
		safeDeath("compulsory-death-accident-work", "work", "accident", "compulsory"),
		safeDeath("compulsory-death-accident-other", "other", "accident", "compulsory"),
		safeDeath("compulsory-death-illness-work", "work", "illness", "compulsory"),
		safeDeath("compulsory-death-illness-other", "other", "illness", "compulsory"),
	];

	// Data for the chart
	const data = {
		labels,
		datasets: [
			{ // OCCUPATIONAL (OBLIGATORY)
				label: `${t("calculator.chart.benefits.occupational-disability")} (${t("calculator.chart.benefits.obligatory")})`,
				subLabel: t("calculator.chart.benefits.compulsory"),
				hiddenInLegend: false,
				data: compulsoryOccupationalData,
				backgroundColor: "#c4d45a",
				stack: "Occupational",
			},
		
			{ // MEDICAL (OBLIGATORY)
				label: `${t("calculator.chart.benefits.medical-invalidity")} (${t("calculator.chart.benefits.obligatory")})`,
				subLabel: t("calculator.chart.benefits.compulsory"),
				hiddenInLegend: false,
				data: compulsoryMedicalData,
				backgroundColor: "#5bb9aa",
				stack: "Medical",
			},
			{ // DEATH (OBLIGATORY)
				label: `${t("calculator.chart.benefits.death")} (${t("calculator.chart.benefits.obligatory")})`,
				subLabel: t("calculator.chart.benefits.compulsory"),
				hiddenInLegend: false,
				data: compulsoryDeathData,
				backgroundColor: "#d4d4d4",
				stack: "Death",
			},
			{ // OCCUPATIONAL
				label: t("calculator.chart.benefits.occupational-disability"),
				subLabel: t("calculator.chart.benefits.employer"),
				hiddenInLegend: false,
				data: employerOccupationalData,
				backgroundColor: "#e0ec89",
				stack: "Occupational",
			},
			{ // MEDICAL
				label: t("calculator.chart.benefits.medical-invalidity"),
				subLabel: t("calculator.chart.benefits.employer"),
				hiddenInLegend: false, // this one will appear in the legend
				data: employerMedicalData,
				backgroundColor: "#7ce0d3",
				stack: "Medical",
			},
			{ // DEATH 
				label: `${t("calculator.chart.benefits.death")}            `,
				subLabel: t("calculator.chart.benefits.employer"),
				hiddenInLegend: false,
				data: employerDeathData,
				backgroundColor: "#f6f6f6",
				stack: "Death",
			},
		],
	};

	// Add these to every dataset
	data.datasets.forEach((ds: ChartDataset<"bar">) => {
		ds.barPercentage = 1;
		ds.categoryPercentage = 0.8;
		ds.borderWidth = 0;
		ds.borderRadius = 1;
	});

	// Legend filter to hide datasets marked with hiddenInLegend.
	// ! We cant find chart.js datatypes. Made it specific to our use case.
	const legendFilter = (legendItem: LegendItem, chartData: typeof data) => {
		if (legendItem.datasetIndex === undefined) {
			return true;
		}
		const ds = chartData.datasets[
			legendItem.datasetIndex
		] as ChartDataset<"bar"> & { hiddenInLegend?: boolean };
		return !ds.hiddenInLegend;
	};

	// Tooltip callback: show subLabel only for employer (avoid "Død (obligatorisk) (Lovpålagt)")
	const compulsoryLabel = t("calculator.chart.benefits.compulsory");
	function tooltipLabel(context: TooltipItem<"bar">) {
		const ds = context.dataset as ChartDataset<"bar"> & { subLabel?: string };
		const val = context.parsed.y || 0;
		const sub =
			ds.subLabel && ds.subLabel !== compulsoryLabel
				? ` (${ds.subLabel})`
				: "";
		return `${ds.label}${sub}: ${val.toLocaleString()} ${currency}`;
	}

	const options: ChartOptions<"bar"> = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "top" as const,
				labels: {
					filter: legendFilter,
					color: "#FFFFFF",
					font: { size: 16, family: "aktiv-grotesk" },
					usePointStyle: true,
					pointStyle: "circle",
					boxWidth: 5,
					boxHeight: 10,
					padding: 30,
				},
			},
			tooltip: {
				mode: "nearest" as const,
				intersect: false,
				callbacks: {
					label: tooltipLabel,
				},
			},
		},
		// Stacking + axis styling
		scales: {
			x: {
				stacked: true,
				grid: {
					display: true,
					color: "#FFFFFF33",
				},
				ticks: {
					autoSkip: false,
					maxTicksLimit: 10,
					minRotation: 0,
					maxRotation: 0,
					color: "#FFFFFF",
					font: { family: "aktiv-grotesk" },
				},
			},
			y: {
				stacked: true,
				grid: {
					display: true,
					color: "#FFFFFF33",
				},
				ticks: {
					color: "#FFFFFF",
					font: { family: "aktiv-grotesk" },
				},
				title: {
					display: true,
					text: t("calculator.chart.benefits.amount"),
					color: "#FFFFFF",
					font: { size: 14, family: "gt-ultra" },
				},
			},
		},
		hover: {
			mode: "nearest" as const,
			intersect: true,
		},
	};

	return <Bar data={data} options={options} />;
}
